import { z } from 'zod';
// =============================================================================
// Aggregate tools
// =============================================================================
//
// Every `aggregate<Entities>` route is the totals sibling of a `list<Entities>`
// route: the same filters, computed in the database over EVERY matching row, with
// an optional breakdown. These builders are what make that one contract rather
// than a family of similar ones - a domain supplies its filters, its dimensions,
// its measures and its group key, and gets the same arguments and the same
// envelope as every other domain. The convention, and why the chat agent needs it,
// is written up in `apps/backend/platform/src/agents/chat/CLAUDE.md` under
// "Aggregate tools".
/**
 * The most groups a breakdown returns. Small on purpose: the caller gets a genuine
 * top-N it can read rather than a page it has to interpret, and the grand `totals`
 * always sits alongside it, so nothing ever needs adding up.
 */
export const AGGREGATE_MAX_GROUPS = 50;
/**
 * An aggregate route's querystring: the paired list route's filters, plus grouping.
 *
 * Grouping is two flat enums rather than one repeatable param. A repeated query
 * param has to accept a scalar or an array, which reaches the chat model as a
 * two-branch `anyOf`, and union-shaped tool arguments correlate with malformed
 * function calls. Two plain enums express "up to two dimensions" with no union.
 */
export function createAggregateQuerySchema(filters, dimension) {
    return filters.extend({
        groupBy: rejectingLists(dimension, 'groupBy')
            .optional()
            .describe('Break the totals down by this dimension. Omit for one grand total.'),
        thenBy: rejectingLists(dimension, 'thenBy')
            .optional()
            .describe('A second dimension, nested under groupBy.'),
    });
}
/**
 * The dimension with an error that names a list as the problem. A model that sends
 * `groupBy: ["clientProject"]` otherwise reads "Invalid option: expected one of ...
 * clientProject ..." - a list that contains what it sent - and has been seen to drop
 * the breakdown and settle for the grand total. Only the error changes: the declared
 * JSON schema stays a plain enum, and a list is still rejected rather than unwrapped,
 * because a transform cannot be serialised into a tool declaration.
 */
function rejectingLists(dimension, argument) {
    return dimension.clone({
        ...dimension.def,
        error: (issue) => {
            if (!Array.isArray(issue.input))
                return undefined;
            const [first] = issue.input;
            const example = typeof first === 'string' ? ` ${argument}: "${first}", not ["${first}"]` : '';
            const second = argument === 'groupBy' ? ' A second dimension goes in thenBy.' : '';
            return `${argument} takes one dimension, not a list. Send a single string:${example || ' one of the listed dimensions'}.${second}`;
        },
    });
}
/**
 * An aggregate route's response envelope.
 *
 * - `totals` covers every matching row, whatever the grouping and whether or not
 *   the breakdown was truncated. It is always the authoritative figure.
 * - `groups` is `null` when no dimension was requested, and otherwise at most
 *   `AGGREGATE_MAX_GROUPS` buckets, largest `count` first.
 * - `filter` restates the population the figures cover, so a caller can quote it.
 *   Its `groupBy` and `thenBy` are scalars, the same shape the query takes: a model
 *   copies what it reads into its next call, and the query rejects a list.
 *
 * `measures` holds the domain's figures. Anything that must never be added
 * together (amounts in different currencies, quantities in different units) is an
 * array with one entry per currency or unit, never a single number. `count` is
 * added here so it is named and typed identically in every domain.
 */
export function createAggregateResponseSchema(parts) {
    const bucket = z.object({ count: z.int().min(0) }).extend(parts.measures.shape);
    return z.object({
        totals: bucket,
        groups: z.array(bucket.extend({ key: parts.key })).nullable(),
        groupsTruncated: z.boolean(),
        filter: parts.filter.extend({ groupBy: parts.dimension.nullable(), thenBy: parts.dimension.nullable() }),
    });
}
